/**
 * The payroll rule language: what a salary component, a statutory contribution or a tax line IS,
 * as data.
 *
 * WHY A DATA LANGUAGE AND NOT CODE. Every amount on a payslip is one `Rule`. A rule is JSON that an
 * administrator can review, version, effective-date and diff; the engine in `@hrms/payroll`
 * evaluates it and knows nothing about any country. "India" is a set of rule sets loaded on top of
 * the engine, exactly as "Germany" would be — there is no `if (country === 'IN')` anywhere in the
 * evaluator, and a test enforces it.
 *
 * WHY A JSON AST AND NOT A FORMULA STRING. A string formula needs a parser, and a parser is where
 * `eval`, precedence surprises and locale-dependent decimal separators come from. An AST is
 * validated by Zod at the edge (CLAUDE.md conventions), can be walked statically to build the
 * dependency graph and to detect cycles before anything is calculated, and can be rendered back to
 * words for the calculation trace. A formula-string editor can be built later as a front-end that
 * emits this AST; the stored form does not change.
 *
 * LAYERING. Rules belong to one of three sections, and a rule may only read its own section or an
 * earlier one: component → statutory → tax. Salary components therefore cannot come to depend on a
 * statutory result, which is what keeps "how is pay structured" separate from "what does the law
 * take from it". The compiler enforces the direction.
 *
 * Money in the language is a decimal STRING, never a number (CLAUDE.md invariant 2). RN-safe: no
 * Node built-ins.
 */

import { z } from 'zod';

const decimalString = z.string().regex(/^-?\d+(\.\d+)?$/, 'Expected a decimal number as a string');

/** Rule and component codes. Upper snake case so a reference is unambiguous next to `in.` / `param.`. */
export const RULE_CODE_PATTERN = /^[A-Z][A-Z0-9_]*$/;
const ruleCode = z.string().regex(RULE_CODE_PATTERN, 'Codes are UPPER_SNAKE_CASE');

export const RULE_SECTIONS = ['component', 'statutory', 'tax'] as const;
export type RuleSection = (typeof RULE_SECTIONS)[number];

export const RULE_COMPONENT_KINDS = [
  'earning',
  'deduction',
  'employer_contribution',
  'reimbursement',
  'perquisite',
  'informational',
] as const;
export type RuleComponentKind = (typeof RULE_COMPONENT_KINDS)[number];

export const ROUNDING_MODES = [
  'half_up',
  'half_down',
  'half_even',
  /** Away from zero — a "round up" in the payroll sense. */
  'up',
  /** Towards zero — truncation. */
  'down',
  'ceil',
  'floor',
] as const;
export type RoundingMode = (typeof ROUNDING_MODES)[number];

/** Round to the nearest multiple of `step`: `1` is whole units, `0.01` is cents, `0.05` is 5-paise. */
export const roundingSchema = z.object({
  mode: z.enum(ROUNDING_MODES),
  step: decimalString.default('0.01'),
});
export type Rounding = z.infer<typeof roundingSchema>;

export const COMPARATORS = ['gt', 'gte', 'lt', 'lte', 'eq', 'neq'] as const;
export type Comparator = (typeof COMPARATORS)[number];

/**
 * How a `slab` expression reads its table.
 *  - `amount`        the containing band's value is the answer (a flat fee by income band).
 *  - `flat_rate`     the containing band's rate applied to the WHOLE input.
 *  - `marginal_rate` each band's rate applied to the part of the input inside that band.
 */
export const SLAB_MODES = ['amount', 'flat_rate', 'marginal_rate'] as const;
export type SlabMode = (typeof SLAB_MODES)[number];

export type Expr =
  | { op: 'const'; value: string }
  /** `CODE`, `CODE#full` (before proration), `in.<input>`, or `param.<name>`. */
  | { op: 'ref'; name: string }
  | { op: 'add' | 'mul' | 'min' | 'max'; args: Expr[] }
  | { op: 'sub' | 'div'; args: [Expr, Expr] }
  /** `of × rate ÷ 100`. Sugar for the commonest thing a rule does, kept readable in the trace. */
  | { op: 'pct'; of: Expr; rate: Expr }
  | { op: 'round'; arg: Expr; mode: RoundingMode; step?: string }
  /** Floor and/or cap, inline — the same limits a rule declares on itself, usable inside a formula. */
  | { op: 'clamp'; arg: Expr; min?: Expr; max?: Expr }
  | { op: 'if'; cond: Cond; then: Expr; else: Expr }
  | { op: 'slab'; on: Expr; table: string; mode: SlabMode }
  /** A keyed value from a `map` table by an employee/entity attribute — e.g. a minimum wage by skill. */
  | { op: 'lookup'; table: string; attr: string; default?: Expr }
  /** The sum of every rule carrying `tag` — how a statutory wage base is declared without naming codes. */
  | { op: 'sum_tag'; tag: string };

export type Cond =
  | { op: 'cmp'; cmp: Comparator; left: Expr; right: Expr }
  | { op: 'and' | 'or'; args: Cond[] }
  | { op: 'not'; arg: Cond }
  /**
   * A boolean input the caller supplies per employee — an opt-in, an exemption, a scheme flag. Absent is
   * not false UNLESS the rule itself declares `default` — OPTIONAL, no default of its own (ADR 0012,
   * "Governing statutory inputs": a new field with no default leaves every EXISTING flag node's canonical
   * text, and therefore its checksum, unchanged). A rule that never sets it keeps today's behaviour
   * exactly: absence throws `missing_input`. One that does treats absence as that declared value instead
   * of an error — for a flag no stored fact can supply yet (item 2, `isExcludedEmployee`), not a way to
   * quietly default an EXISTING scheme flag.
   */
  | { op: 'flag'; name: string; default?: boolean }
  /** An attribute equals one of these strings — a state code, a grade, a skill level. */
  | { op: 'attr_in'; attr: string; values: string[] };

export const exprSchema: z.ZodType<Expr> = z.lazy(() =>
  z.discriminatedUnion('op', [
    z.object({ op: z.literal('const'), value: decimalString }),
    z.object({ op: z.literal('ref'), name: z.string().min(1).max(120) }),
    z.object({
      op: z.enum(['add', 'mul', 'min', 'max']),
      args: z.array(exprSchema).min(1).max(64),
    }),
    z.object({ op: z.enum(['sub', 'div']), args: z.tuple([exprSchema, exprSchema]) }),
    z.object({ op: z.literal('pct'), of: exprSchema, rate: exprSchema }),
    z.object({
      op: z.literal('round'),
      arg: exprSchema,
      mode: z.enum(ROUNDING_MODES),
      step: decimalString.optional(),
    }),
    z.object({
      op: z.literal('clamp'),
      arg: exprSchema,
      min: exprSchema.optional(),
      max: exprSchema.optional(),
    }),
    z.object({ op: z.literal('if'), cond: condSchema, then: exprSchema, else: exprSchema }),
    z.object({
      op: z.literal('slab'),
      on: exprSchema,
      table: z.string().min(1).max(80),
      mode: z.enum(SLAB_MODES),
    }),
    z.object({
      op: z.literal('lookup'),
      table: z.string().min(1).max(80),
      attr: z.string().min(1).max(80),
      default: exprSchema.optional(),
    }),
    z.object({ op: z.literal('sum_tag'), tag: z.string().min(1).max(80) }),
  ]),
) as z.ZodType<Expr>;

export const condSchema: z.ZodType<Cond> = z.lazy(() =>
  z.discriminatedUnion('op', [
    z.object({
      op: z.literal('cmp'),
      cmp: z.enum(COMPARATORS),
      left: exprSchema,
      right: exprSchema,
    }),
    z.object({ op: z.enum(['and', 'or']), args: z.array(condSchema).min(1).max(32) }),
    z.object({ op: z.literal('not'), arg: condSchema }),
    z.object({
      op: z.literal('flag'),
      name: z.string().min(1).max(80),
      default: z.boolean().optional(),
    }),
    z.object({
      op: z.literal('attr_in'),
      attr: z.string().min(1).max(80),
      values: z.array(z.string().min(1).max(80)).min(1).max(200),
    }),
  ]),
) as z.ZodType<Cond>;

/** How a rule's value comes about. */
export const ruleCalcSchema = z.discriminatedUnion('type', [
  z.object({ type: z.literal('expr'), expr: exprSchema }),
  /**
   * The residual: `target` less everything else in `group`, never below zero. A shortfall — the rest
   * of the group already exceeds the target — is reported in the trace, not hidden by a negative.
   */
  z.object({ type: z.literal('balancing'), target: exprSchema, group: z.string().min(1).max(80) }),
]);
export type RuleCalc = z.infer<typeof ruleCalcSchema>;

export const PRORATION_MODES = ['none', 'payable_days'] as const;

/**
 * A rule tagged `basis:<key>` is a STATUTORY BASIS (ADR 0012, "Statutory bases"): a wage or amount a scheme is computed on, which a
 * run records alongside its result. The KEY is the pack's own vocabulary; the engine and the result read only this prefix, so no
 * country's basis names appear outside its pack. A tag is otherwise an ordinary label.
 */
export const BASIS_TAG_PREFIX = 'basis:';
export const BASIS_KEY_PATTERN = /^[a-z][a-z0-9_]{0,39}$/;

/** The basis key a tag declares, or null for an ordinary tag (or a malformed basis tag, which the rule schema refuses). */
export function basisKeyOfTag(tag: string): string | null {
  if (!tag.startsWith(BASIS_TAG_PREFIX)) return null;
  const key = tag.slice(BASIS_TAG_PREFIX.length);
  return BASIS_KEY_PATTERN.test(key) ? key : null;
}

/**
 * A rule tagged `constraint:<key>` is a CONSTRAINT (ADR 0012, "Company-editable options"): a rule whose value is a VIOLATION
 * amount, and any non-zero value means the calculation broke a limit the pack declared (a wage below a floor, a sum above a cap).
 * It mirrors `basis:`: the KEY is the pack's own vocabulary and the engine reads only this prefix, so no country's constraint
 * name appears outside its pack. A tagged rule that evaluates to zero is satisfied and says nothing; a non-zero one is surfaced by
 * the adapter as a run diagnostic and by simulation and readiness as a blocker. This schema declares no constraint.
 */
export const CONSTRAINT_TAG_PREFIX = 'constraint:';
export const CONSTRAINT_KEY_PATTERN = /^[a-z][a-z0-9_]{0,39}$/;

/** The constraint key a tag declares, or null for an ordinary tag (or a malformed constraint tag, which the rule schema refuses). */
export function constraintKeyOfTag(tag: string): string | null {
  if (!tag.startsWith(CONSTRAINT_TAG_PREFIX)) return null;
  const key = tag.slice(CONSTRAINT_TAG_PREFIX.length);
  return CONSTRAINT_KEY_PATTERN.test(key) ? key : null;
}

/**
 * Who owns a parameter's value (ADR 0012, "Parameter policy").
 *
 *  - `statutory`          fixed by the published rule version alone. Never `configurable`, never `overridable`.
 *  - `statutory_bounded`  a company may set it only within bounds the compliance owner supplies as DATA (`min`/`max`, or `choices`).
 *  - `company_policy`     the company's own choice; the platform states no limit.
 *
 * A parameter with NO entry is unclassified: nothing is claimed about it, and nothing changes for it. No parameter of any
 * country pack is classified by this schema, and no bound is ever invented by engineering.
 */
export const PARAM_POLICY_CLASSES = ['statutory', 'statutory_bounded', 'company_policy'] as const;
export const paramPolicyEntrySchema = z
  .object({
    class: z.enum(PARAM_POLICY_CLASSES),
    min: decimalString.optional(),
    max: decimalString.optional(),
    /**
     * The only values a company may pick (a `statutory_bounded` alternative to `min`/`max`, exclusive with them): a closed
     * election such as `['0', '1']`. Supplied as DATA by the compliance owner, like a bound. Decimal strings, compared
     * numerically, so `1` and `1.0` are the same choice.
     */
    choices: z.array(decimalString).min(1).max(20).optional(),
  })
  .superRefine((entry, ctx) => {
    const bounded = entry.class === 'statutory_bounded';
    const hasRange = entry.min !== undefined || entry.max !== undefined;
    const hasChoices = entry.choices !== undefined;
    if (bounded && !hasRange && !hasChoices) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'A statutory_bounded parameter needs a min, a max, both, or choices',
      });
    }
    if (!bounded && (hasRange || hasChoices)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: `Bounds and choices apply only to statutory_bounded, not ${entry.class}`,
      });
    }
    if (hasRange && hasChoices) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'A parameter is bounded by min/max or by choices, not both',
      });
    }
    if (
      entry.choices !== undefined &&
      entry.choices.some(
        (a, i) => entry.choices!.findIndex((b) => compareDecimalStrings(a, b) === 0) !== i,
      )
    ) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'choices repeats a value' });
    }
    if (
      entry.min !== undefined &&
      entry.max !== undefined &&
      compareDecimalStrings(entry.min, entry.max) > 0
    ) {
      ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'min is greater than max' });
    }
  });
export type ParamPolicyEntry = z.infer<typeof paramPolicyEntrySchema>;

/** Exact comparison of two decimal strings (no floating point): negative, zero or positive. */
export function compareDecimalStrings(a: string, b: string): number {
  const split = (v: string) => {
    const negative = v.startsWith('-');
    const [int = '0', frac = ''] = (negative ? v.slice(1) : v).split('.');
    return { negative, int: int.replace(/^0+(?=\d)/, ''), frac: frac.replace(/0+$/, '') };
  };
  const x = split(a);
  const y = split(b);
  const zero = (d: ReturnType<typeof split>) => d.int === '0' && d.frac === '';
  if (zero(x) && zero(y)) return 0;
  const sign = (d: ReturnType<typeof split>) => (zero(d) ? 0 : d.negative ? -1 : 1);
  if (sign(x) !== sign(y)) return sign(x) < sign(y) ? -1 : 1;
  const magnitude =
    x.int.length !== y.int.length
      ? x.int.length - y.int.length
      : x.int !== y.int
        ? x.int < y.int
          ? -1
          : 1
        : x.frac.padEnd(y.frac.length, '0') === y.frac.padEnd(x.frac.length, '0')
          ? 0
          : x.frac.padEnd(y.frac.length, '0') < y.frac.padEnd(x.frac.length, '0')
            ? -1
            : 1;
  return x.negative ? -Math.sign(magnitude) : Math.sign(magnitude);
}

const ruleShape = z.object({
  code: ruleCode,
  name: z.string().min(1).max(160),
  section: z.enum(RULE_SECTIONS),
  kind: z.enum(RULE_COMPONENT_KINDS),
  /** Labels other rules aggregate by (`sum_tag`) — how "the wage base" is a declaration, not a code. */
  tags: z.array(z.string().min(1).max(80)).max(20).default([]),
  /** Membership of a balancing group, so a residual rule can name what it balances against. */
  group: z.string().min(1).max(80).optional(),
  calc: ruleCalcSchema,
  /** When false the rule contributes zero and the trace says it was skipped, and why. */
  when: condSchema.optional(),
  floor: exprSchema.optional(),
  cap: exprSchema.optional(),
  rounding: roundingSchema.optional(),
  /** Whether unpaid days scale the result. Computed once as a single factor for every such rule. */
  proration: z.enum(PRORATION_MODES).default('none'),
  /** Named tunables, referenced as `param.<name>` and the only thing an override may change. */
  params: z.record(z.string().regex(/^[a-z][a-zA-Z0-9_]*$/), decimalString).default({}),
  /**
   * The subset of `params` a LEGAL ENTITY may set as effective-dated configuration. Empty means none.
   * Opt-in and explicit: a param is never configurable merely because it is defined. Distinct from
   * `overridable` — configuration is the entity's standing setting, an override is an exception.
   */
  configurable: z.array(z.string()).max(50).default([]),
  /**
   * The subset of `params` an override (for a legal entity or for one employment) may change.
   * Empty means nothing may be overridden.
   */
  overridable: z.array(z.string()).max(50).default([]),
  /**
   * Who owns each param's value (see `paramPolicyEntrySchema`). OPTIONAL with no default, so a definition written before it
   * existed parses and hashes exactly as it did: an absent policy changes no canonical text.
   */
  paramPolicy: z
    .record(z.string().regex(/^[a-z][a-zA-Z0-9_]*$/), paramPolicyEntrySchema)
    .optional(),
  /** Where this comes from, for a payslip line or a reviewer: "12% of PF wage, capped". */
  description: z.string().max(400).optional(),
});

/** A tier may only name params the rule really defines — a typo must not silently grant nothing. */
export const ruleSchema = ruleShape.superRefine((rule, ctx) => {
  for (const tier of ['configurable', 'overridable'] as const) {
    for (const name of rule[tier]) {
      if (!(name in rule.params)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [tier],
          message: `'${name}' is listed as ${tier} but ${rule.code} defines no such param`,
        });
      }
    }
  }
  for (const tag of rule.tags) {
    if (tag.startsWith(BASIS_TAG_PREFIX) && basisKeyOfTag(tag) === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['tags'],
        message: `'${tag}' is not a valid statutory basis tag: expected ${BASIS_TAG_PREFIX}<key>, key ${BASIS_KEY_PATTERN}`,
      });
    }
  }
  for (const tag of rule.tags) {
    if (tag.startsWith(CONSTRAINT_TAG_PREFIX) && constraintKeyOfTag(tag) === null) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['tags'],
        message: `'${tag}' is not a valid constraint tag: expected ${CONSTRAINT_TAG_PREFIX}<key>, key ${CONSTRAINT_KEY_PATTERN}`,
      });
    }
  }
  for (const [name, policy] of Object.entries(rule.paramPolicy ?? {})) {
    if (!(name in rule.params)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['paramPolicy', name],
        message: `'${name}' has a policy but ${rule.code} defines no such param`,
      });
      continue;
    }
    // A statutory value is fixed by the published version: no company tier may reach it.
    if (policy.class === 'statutory') {
      for (const tier of ['configurable', 'overridable'] as const) {
        if (rule[tier].includes(name)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [tier],
            message: `'${name}' is statutory in ${rule.code} and cannot be ${tier}`,
          });
        }
      }
    }
  }
});

/**
 * Why a company-supplied value for `param` is outside the rule's declared policy, or null when it is not. Only a
 * `statutory` param (never settable) and a `statutory_bounded` one (outside its bounds, or not one of its choices) can refuse a value: an unclassified
 * or `company_policy` param never does. Used where a value is WRITTEN or REPORTED, never where a frozen snapshot is replayed.
 */
export function paramPolicyViolation(
  rule: Pick<Rule, 'code' | 'paramPolicy'>,
  param: string,
  value: string,
): string | null {
  const policy = rule.paramPolicy?.[param];
  if (!policy) return null;
  if (policy.class === 'statutory') {
    return `${rule.code}.${param} is statutory: the published rule version fixes it, and a company cannot set it`;
  }
  if (policy.class === 'statutory_bounded') {
    if (
      policy.choices !== undefined &&
      !policy.choices.some((choice) => compareDecimalStrings(value, choice) === 0)
    ) {
      return `${rule.code}.${param} is ${value}, which is not one of the values the rule allows (${policy.choices.join(', ')})`;
    }
    if (policy.min !== undefined && compareDecimalStrings(value, policy.min) < 0) {
      return `${rule.code}.${param} is ${value}, below the minimum ${policy.min} the rule allows`;
    }
    if (policy.max !== undefined && compareDecimalStrings(value, policy.max) > 0) {
      return `${rule.code}.${param} is ${value}, above the maximum ${policy.max} the rule allows`;
    }
  }
  return null;
}
export type Rule = z.infer<typeof ruleSchema>;

const bandSchema = z.object({
  from: decimalString,
  /** Exclusive upper bound; null is the open-ended top band. */
  upTo: decimalString.nullable(),
  value: decimalString,
});

export const ruleTableSchema = z.discriminatedUnion('type', [
  /** Contiguous half-open bands `[from, upTo)`. Gaps and overlaps are rejected at compile time. */
  z.object({ type: z.literal('bands'), bands: z.array(bandSchema).min(1).max(200) }),
  z.object({ type: z.literal('map'), entries: z.record(z.string().min(1), decimalString) }),
]);
export type RuleTable = z.infer<typeof ruleTableSchema>;

/**
 * A reverse calculation: hold one output at a target and solve for an input.
 *
 * e.g. `unknown: 'gross'`, `targetRef: 'NET_PAY'`, `targetInput: 'net'` answers "what gross gives
 * this take-home?" by evaluating the FORWARD rules repeatedly — there is no second, inverted copy of
 * the arithmetic to drift out of step with the first.
 */
export const reverseSpecSchema = z
  .object({
    code: ruleCode,
    /** The input to solve for. */
    unknown: z.string().regex(/^[a-z][a-zA-Z0-9_]*$/),
    /** The rule whose value is held at the target. */
    targetRef: ruleCode,
    /** Where the target comes from: an input the caller supplies… */
    targetInput: z
      .string()
      .regex(/^[a-z][a-zA-Z0-9_]*$/)
      .optional(),
    /** …or a constant (zero, for "find the input at which this gap closes"). Exactly one is set. */
    targetValue: decimalString.optional(),
    min: decimalString.default('0'),
    max: decimalString,
    /** Stop when the target is met to within this — normally the currency's smallest unit. */
    tolerance: decimalString.default('0.01'),
    /**
     * Other specs solved FIRST, at every probe of this one, and fed in as inputs. This is how a
     * fixed point that itself depends on the unknown is expressed without a circular rule: e.g. a
     * wage floor that depends on gross is solved inside the search for the gross that hits a net.
     */
    nested: z.array(ruleCode).max(5).default([]),
    /**
     * EXACT TARGET (ADR 0015). When true the solve must land ON the target — tolerance zero — or refuse with
     * `target_not_reachable_exactly`, naming the nearest reachable values below and above; it never returns a
     * nearby value and calls it a solution. ABSENT (not `false`) is the earlier behaviour, nearest reachable
     * within `tolerance`: it stays optional, with no default, so a definition frozen before ADR 0015 keeps its
     * canonical text — and therefore its checksum and its replay — byte for byte.
     */
    exactTarget: z.boolean().optional(),
    /**
     * A rule that is zero exactly when the unknown meets the floor the composition enforces (the wage floor) and
     * positive below it. When an exact solve's target lies below what any package meeting the floor can reach, the
     * solver uses it to name the smallest package that can (`package_below_minimum_wage`). Only read with
     * `exactTarget`; optional with no default for the same reason.
     */
    floorRef: ruleCode.optional(),
  })
  .refine((spec) => (spec.targetInput === undefined) !== (spec.targetValue === undefined), {
    message: 'Set exactly one of targetInput and targetValue',
  });
export type ReverseSpec = z.infer<typeof reverseSpecSchema>;

/** One versionable body of rules. Effective dating wraps it (see `ruleSetVersionSchema`). */
/**
 * The version of the rule LANGUAGE a definition is written in. Changing what an operator means is a
 * new language version; version 1 is never edited, so a stored definition evaluates the same way
 * forever. The engine refuses a version it does not implement.
 */
export const PAYROLL_LANGUAGE_VERSION = 1;

/**
 * Where a published rule version's figures come from (ADR 0012, "Rule provenance"). Structured, so a reviewer can tell a
 * statute from a workbook default without reading prose. It lives INSIDE the definition: sealed and immutable with the
 * version, and frozen into every rules snapshot. Recording a compliance review is therefore a NEW version, never an edit.
 *
 * OPTIONAL with no default: a definition without provenance parses and hashes as it always did. Nothing here is a statutory
 * figure, and nothing requires provenance yet (whether unreviewed provenance blocks publish or engine entry is an owner
 * decision).
 */
export const PROVENANCE_KINDS = [
  'statute',
  'notification',
  'circular',
  'owner_decision',
  'engineering_convention',
  'workbook_default',
] as const;
/**
 * `pending_review` (item 3, ADR 0012's open question "whether unreviewed provenance should block
 * publication or engine entry"): a queued-for-review state, distinct from `unreviewed` (no review has
 * even been requested) — additive, backward compatible. `unreviewed` and `compliance_reviewed` keep
 * their EXACT existing meaning; nothing that already reads either value needs to change.
 * `reviewedByRole`/`reviewRef`/`reviewedOn` stay OPTIONAL for `pending_review` (the `superRefine` below
 * only ever required them for `compliance_reviewed`, so no change to it was needed for this addition).
 */
export const PROVENANCE_REVIEW_STATUSES = [
  'unreviewed',
  'pending_review',
  'compliance_reviewed',
] as const;
const provenanceDate = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Expected YYYY-MM-DD');
export const ruleProvenanceEntrySchema = z
  .object({
    kind: z.enum(PROVENANCE_KINDS),
    /** The instrument, section or document this figure comes from, or what states it (for a non-statutory kind). */
    citation: z.string().min(1).max(400),
    /** An external reference or content hash of the source document. */
    documentRef: z.string().min(1).max(400).optional(),
    effectiveFrom: provenanceDate.optional(),
    reviewStatus: z.enum(PROVENANCE_REVIEW_STATUSES),
    /** A ROLE ("compliance"), never a person. Required once reviewed. */
    reviewedByRole: z.string().min(1).max(80).optional(),
    reviewRef: z.string().min(1).max(200).optional(),
    reviewedOn: provenanceDate.optional(),
    /** The rules (`EPF_ER`) or params (`EPF_ER.rate`) this entry supports. Empty: the whole set. */
    appliesTo: z.array(z.string().min(1).max(120)).max(100).optional(),
  })
  .superRefine((entry, ctx) => {
    if (entry.reviewStatus === 'compliance_reviewed') {
      for (const field of ['reviewedByRole', 'reviewRef', 'reviewedOn'] as const) {
        if (entry[field] === undefined) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [field],
            message: `${field} is required for a compliance_reviewed source`,
          });
        }
      }
    }
  });
export type RuleProvenanceEntry = z.infer<typeof ruleProvenanceEntrySchema>;

/**
 * `none`: no provenance recorded. `unreviewed`: some entry has not been compliance-reviewed — this
 * bucket also covers `pending_review` (item 3): the three-way split needs no fourth bucket, because
 * every caller of `provenanceStatusOf` (the engine-entry guard included) only ever needs to ask "is
 * EVERY entry fully reviewed, or not" — `none`/`unreviewed`/`pending_review` are all "not yet", and
 * collapsing them costs nothing a caller reads today. A caller that needs to tell `pending_review`
 * apart from a plain `unreviewed` reads `reviewStatus` on the entries directly; nothing does yet.
 * `reviewed`: every entry is `compliance_reviewed`.
 */
export function provenanceStatusOf(definition: {
  provenance?: readonly RuleProvenanceEntry[] | undefined;
}): 'none' | 'unreviewed' | 'reviewed' {
  const entries = definition.provenance ?? [];
  if (entries.length === 0) return 'none';
  return entries.every((e) => e.reviewStatus === 'compliance_reviewed') ? 'reviewed' : 'unreviewed';
}

// ---------------------------------------------------------------------------
// Company components: the registry of payroll natures a pack declares (ADR 0012, "Company components")
// ---------------------------------------------------------------------------

/** A nature's key, as the pack names it and a tenant's component refers to it. The platform defines none. */
export const NATURE_KEY_PATTERN = /^[a-z][a-z0-9_]{0,39}$/;

/** Whether a nature's amount is part of a wage base: always, never, or the company's own election (which can only WIDEN). */
export const NATURE_BASE_MEMBERSHIPS = ['included', 'excluded', 'elective'] as const;

/** Tag on a derived rule naming the company component it stands for: `component:<CODE>` (the code a payslip line carries). */
export const COMPONENT_TAG_PREFIX = 'component:';

/** The origin a derived (not published) definition carries in a rules snapshot. */
export const COMPANY_COMPONENTS_ORIGIN = 'company_components' as const;
/** The rule-set code of the definition derived from a tenant's company components. */
export const COMPANY_COMPONENTS_SET_CODE = 'COMPANY_COMPONENTS';

/**
 * ONE payroll nature: how a company's own earning or other component is to be treated by the pack's rules. Everything here is DATA
 * the compliance owner supplies; the platform defines no nature and gives no key a meaning. A tenant classifies a component by
 * naming a key; a component with no key is unclassified and is never read as included or excluded.
 */
export const componentNatureSchema = z.object({
  key: z.string().regex(NATURE_KEY_PATTERN),
  label: z.string().min(1).max(120),
  /** Which component kinds may carry this nature. */
  kinds: z.array(z.enum(RULE_COMPONENT_KINDS)).min(1).max(6),
  /** The balancing / aggregation group a rule of this nature joins (the pack's own vocabulary), when it joins one. */
  group: z.string().min(1).max(80).optional(),
  /**
   * Labels every rule of this nature carries UNCONDITIONALLY, in the pack's own vocabulary: how it counts toward gross or taxable
   * earnings is a tag the pack's `sum_tag` rules read.
   */
  tags: z.array(z.string().min(1).max(80)).max(20).optional(),
  /**
   * Membership per wage base (the base's key is the tag the pack's `sum_tag` reads): `included`, `excluded`, or `elective` (a
   * company choice, which can only widen the base). A base not listed is not touched.
   */
  bases: z.record(z.string().min(1).max(80), z.enum(NATURE_BASE_MEMBERSHIPS)).optional(),
  /**
   * Whether unpaid days scale a rule of this nature (`payable_days`) or not (`none`). Data the pack supplies: an absent value means
   * the rule is NOT prorated, never a silent choice on the platform's part.
   */
  proration: z.enum(PRORATION_MODES).optional(),
  /** A `provenance[].appliesTo` entry that supports this nature, so its figures can be traced. */
  provenanceRef: z.string().min(1).max(120).optional(),
});
export type ComponentNature = z.infer<typeof componentNatureSchema>;

/**
 * The names the pack's own rules give the things a derived company component must refer to (ADR 0012, "Company components"): the
 * input that carries monthly cost, the rule a percentage-of-base component reads, and the rule a balancing component closes on.
 * The pack supplies them so generic code names none. Each is optional: a component whose calculation needs one the pack does not
 * name is refused, never guessed.
 */
export const componentReferencesSchema = z.object({
  ctcInput: z
    .string()
    .regex(/^[a-z][a-zA-Z0-9_]*$/)
    .optional(),
  basicRule: ruleCode.optional(),
  balancingTarget: ruleCode.optional(),
});
export type ComponentReferences = z.infer<typeof componentReferencesSchema>;

/** The attribute a definition that carries derived company components reads to know which structure template an employee is on. */
export const COMPANY_TEMPLATE_ATTRIBUTE = 'structureTemplate';

/** The text a payslip line's `basis` starts with when the line stands for a company component (ADR 0016), followed by its rule. */
export const COMPANY_COMPONENT_BASIS_PREFIX = 'Company component ';

// ---------------------------------------------------------------------------
// Fact classes: a pack's own statement of what KIND of fact each one is (ADR 0016)
// ---------------------------------------------------------------------------

/**
 * `applicability`  whether a rule applies to a person at all (a legal status the employee holds or does not);
 * `election`       a choice a person or company makes within what the rule allows;
 * `data`           a plain measurement or record the rules read.
 * The classes are MECHANISM. Which fact belongs to which is a compliance classification, supplied by the pack as data; this
 * schema classifies none, and a fact no pack classifies keeps behaving exactly as it always did.
 */
export const PAYROLL_FACT_CLASSES = ['applicability', 'election', 'data'] as const;
export type PayrollFactClass = (typeof PAYROLL_FACT_CLASSES)[number];

/** One fact's declared class, and whether an entity-level default may stand in for a value the employee has not recorded. */
export const factClassEntrySchema = z.object({
  name: z.string().regex(/^[a-z][a-zA-Z0-9_]*$/),
  kind: z.enum(['input', 'attribute', 'flag']),
  class: z.enum(PAYROLL_FACT_CLASSES),
  /**
   * Whether an explicit, effective-dated entity default may supply this fact. ABSENT means "keep today's rule" (the fact's
   * source decides), so declaring a class alone never changes behaviour; `true` and `false` are the pack's own decision.
   */
  defaultable: z.boolean().optional(),
});
export type FactClassEntry = z.infer<typeof factClassEntrySchema>;

export const ruleSetDefinitionSchema = z.object({
  languageVersion: z.number().int().min(1).default(PAYROLL_LANGUAGE_VERSION),
  code: ruleCode,
  /** The section every rule in this set belongs to — a set never mixes layers. */
  section: z.enum(RULE_SECTIONS),
  /** ISO 3166-1 alpha-2, or `*` for a country-independent set. Data, never read by the evaluator. */
  country: z.string().regex(/^([A-Z]{2}|\*)$/),
  /** A sub-national or scheme scope — a state code, a province — or null for the whole country. */
  jurisdiction: z.string().min(1).max(40).nullable().default(null),
  /** Inputs the caller must supply, referenced as `in.<name>`. Anything else is a compile error. */
  inputs: z
    .array(z.string().regex(/^[a-z][a-zA-Z0-9_]*$/))
    .max(100)
    .default([]),
  /** Attribute names the caller supplies as strings, for `lookup` and `attr_in`. */
  attributes: z
    .array(z.string().regex(/^[a-z][a-zA-Z0-9_]*$/))
    .max(50)
    .default([]),
  /** Boolean flag names the caller supplies. */
  flags: z
    .array(z.string().regex(/^[a-z][a-zA-Z0-9_]*$/))
    .max(50)
    .default([]),
  tables: z.record(z.string().regex(/^[a-z][a-zA-Z0-9_]*$/), ruleTableSchema).default({}),
  defaultRounding: roundingSchema.default({ mode: 'half_up', step: '0.01' }),
  rules: z.array(ruleSchema).max(500),
  reverse: z.array(reverseSpecSchema).max(20).default([]),
  /** Where the figures come from (see `ruleProvenanceEntrySchema`). Optional, no default: it never alters an existing hash. */
  provenance: z.array(ruleProvenanceEntrySchema).max(50).optional(),
  /**
   * The registry of payroll natures a company's own components may take (see `componentNatureSchema`). Optional, no default: a
   * definition without one parses and hashes as it always did, and a pack that declares none lets no company component be
   * classified.
   */
  componentNatures: z.array(componentNatureSchema).max(60).optional(),
  /** The pack's own names for what a derived company component refers to (see `componentReferencesSchema`). Optional, no default. */
  componentReferences: componentReferencesSchema.optional(),
  /** The class of each fact the pack declares (see `factClassEntrySchema`). Optional, no default: absent changes nothing and no hash. */
  factClasses: z.array(factClassEntrySchema).max(100).optional(),
});
export type RuleSetDefinition = z.infer<typeof ruleSetDefinitionSchema>;
export type RuleSetDefinitionInput = z.input<typeof ruleSetDefinitionSchema>;

/**
 * What is wrong with a definition's registry of natures, in words (empty when it is sound): a repeated key, or a `provenanceRef`
 * that no `provenance[].appliesTo` names. Kept apart from the schema so the definition stays a plain object schema; a pack draft
 * is validated with it before it can be published.
 */
export function componentNatureProblems(
  definition: Pick<RuleSetDefinition, 'componentNatures' | 'provenance'>,
): string[] {
  const problems: string[] = [];
  const seen = new Set<string>();
  const refs = new Set((definition.provenance ?? []).flatMap((p) => p.appliesTo ?? []));
  for (const nature of definition.componentNatures ?? []) {
    if (seen.has(nature.key))
      problems.push(`the nature '${nature.key}' is declared more than once`);
    seen.add(nature.key);
    if (nature.provenanceRef !== undefined && !refs.has(nature.provenanceRef)) {
      problems.push(
        `the nature '${nature.key}' cites provenance '${nature.provenanceRef}', which no provenance entry applies to`,
      );
    }
  }
  return problems;
}

/** What is wrong with a definition's fact classes, in words (empty when sound): a fact declared twice. */
export function factClassProblems(definition: Pick<RuleSetDefinition, 'factClasses'>): string[] {
  const seen = new Set<string>();
  const problems: string[] = [];
  for (const entry of definition.factClasses ?? []) {
    const key = `${entry.kind}:${entry.name}`;
    if (seen.has(key))
      problems.push(`the fact '${entry.name}' (${entry.kind}) is classified more than once`);
    seen.add(key);
  }
  return problems;
}
